import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  fetchShowcaseBets,
  selectShowcaseBets,
  selectBetHouseLoading,
  selectBetHouseError,
} from "../../redux/slices/betHouseSlice";
import { selectAppIsAuthenticated } from "../redux/appAuthSlice";
import FloorIndex from "../components/bondhouse/floor/FloorIndex";
import FloorDetail from "../components/bondhouse/floor/FloorDetail";
import BetChatModal from "../components/bondhouse/BetChatModal";
import {
  filterHousesByDiscovery,
  getHouseDiscoveryCounts,
  rankHousesByHeat,
  searchHouses,
  sortHouses,
} from "../utils/bondhouseHelpers";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { usePersistedPanelSize } from "../hooks/usePersistedPanelSize";
import { useHorizontalPanelResize } from "../hooks/useHorizontalPanelResize";
import FeedRefreshBanner from "../../components/FeedRefreshBanner";
import { isNetworkOffline } from "../../utils/requireNetwork";
import "../bondhouse.css";

const FOCUS_QUERY = "h";
const FLOOR_MOBILE_BREAKPOINT = "(max-width: 900px)";
const FLOOR_INDEX_DEFAULT = 340;
const FLOOR_INDEX_MIN = 260;
const FLOOR_INDEX_MAX = 560;

const BondHouse = () => {
  const dispatch = useDispatch();
  const houses = useSelector(selectShowcaseBets);
  const isLoading = useSelector(selectBetHouseLoading);
  const error = useSelector(selectBetHouseError);
  const isAuthenticated = useSelector(selectAppIsAuthenticated);

  const [params, setParams] = useSearchParams();
  const selectedId = params.get(FOCUS_QUERY) || null;

  const [houseFilter, setHouseFilter] = useState("all");
  const [sortBy, setSortBy] = useState("bettors");
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [chatHouse, setChatHouse] = useState(null);
  const isMobileFloor = useMediaQuery(FLOOR_MOBILE_BREAKPOINT);
  const {
    size: indexWidth,
    setClampedSize: setIndexWidth,
    reset: resetIndexWidth,
    minSize: indexMin,
    maxSize: indexMax,
  } = usePersistedPanelSize("bb-floor-index-w", {
    defaultSize: FLOOR_INDEX_DEFAULT,
    minSize: FLOOR_INDEX_MIN,
    maxSize: FLOOR_INDEX_MAX,
  });
  const onIndexResizePointerDown = useHorizontalPanelResize(
    indexWidth,
    setIndexWidth,
    "start"
  );
  const floorGridStyle = !isMobileFloor
    ? { "--floor-index-w": `${indexWidth}px` }
    : undefined;

  const load = useCallback(async ({ force = false } = {}) => {
    if (!force && isNetworkOffline()) return;

    const hasCachedFeed = houses.length > 0;
    setIsUpdating(true);
    if (!hasCachedFeed) setRefreshFailed(false);

    try {
      await dispatch(fetchShowcaseBets({ page: 1, limit: 24, force })).unwrap();
      setRefreshFailed(false);
    } catch {
      if (hasCachedFeed) setRefreshFailed(true);
    } finally {
      setIsUpdating(false);
    }
  }, [dispatch, houses.length]);

  useEffect(() => {
    load();
  }, [load]);

  const discoveryCounts = useMemo(
    () => getHouseDiscoveryCounts(houses),
    [houses]
  );

  const filtered = useMemo(() => {
    let list = filterHousesByDiscovery(houses, houseFilter);
    list = searchHouses(list, searchQuery);
    return sortHouses(list, sortBy);
  }, [houses, houseFilter, searchQuery, sortBy]);

  useEffect(() => {
    if (!filtered.length) return;
    const stillVisible =
      selectedId && filtered.some((h) => h._id === selectedId);
    if (!stillVisible) {
      const next = rankHousesByHeat(filtered)[0]?._id;
      if (next) {
        const np = new URLSearchParams(params);
        np.set(FOCUS_QUERY, next);
        setParams(np, { replace: true });
      }
    }
  }, [filtered, selectedId, params, setParams]);

  const selectedHouse = useMemo(() => {
    if (!selectedId) return null;
    return houses.find((h) => h._id === selectedId) || null;
  }, [houses, selectedId]);

  const onSelect = useCallback(
    (id) => {
      const np = new URLSearchParams(params);
      if (id) np.set(FOCUS_QUERY, id);
      else np.delete(FOCUS_QUERY);
      setParams(np, { replace: true });
    },
    [params, setParams]
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load({ force: true });
    setRefreshing(false);
  }, [load]);

  const onChatOpen = useCallback((house) => {
    if (house) setChatHouse(house);
  }, []);

  return (
    <div className="floor-shell floor-shell--ledger">
      <FeedRefreshBanner
        isUpdating={isUpdating}
        refreshFailed={refreshFailed}
        onRetry={() => load({ force: true })}
      />
      {error && !isLoading && houses.length === 0 && (
        <div className="floor-error" role="alert">
          Could not load houses. Try again in a moment.
        </div>
      )}

      <div className="floor-grid" style={floorGridStyle}>
        <div className="floor-index-pane">
          <FloorIndex
            houses={filtered}
            selectedId={selectedId}
            onSelect={onSelect}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            houseFilter={houseFilter}
            onHouseFilterChange={setHouseFilter}
            discoveryCounts={discoveryCounts}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onRefresh={onRefresh}
            refreshing={refreshing}
            isLoading={isLoading}
            totalHouses={houses.length}
          />
          {!isMobileFloor && (
            <div
              className="floor-resize-handle"
              role="separator"
              aria-orientation="vertical"
              aria-valuenow={indexWidth}
              aria-valuemin={indexMin}
              aria-valuemax={indexMax}
              aria-label="Resize floor ledger"
              title="Drag to resize · double-click to reset"
              onPointerDown={onIndexResizePointerDown}
              onDoubleClick={resetIndexWidth}
            />
          )}
        </div>
        <FloorDetail
          house={selectedHouse}
          emptyFloor={!isLoading && houses.length === 0}
          isAuthenticated={isAuthenticated}
          onChatOpen={onChatOpen}
        />
      </div>

      <BetChatModal
        open={Boolean(chatHouse)}
        onClose={() => setChatHouse(null)}
        house={chatHouse}
      />
    </div>
  );
};

export default BondHouse;
